/**
 * AstralProjectionStateSystem — body/projection separation for Astral Projection.
 *
 * Astral Projection (astral_projection) separates the player's rendered body
 * from their control. For the duration of the projection:
 * - The physical body is locked at the departure position and invincible.
 * - A "projection" entity moves freely through walls under player control.
 * - Enemies marked by the projection during the window are tagged for the
 *   follow-up burst when the player snaps back to their body.
 *
 * Actual entity movement, invincibility flags, and visual rendering are
 * performed through the injected ProjectionEntityAdapter, keeping this
 * module free of Phaser scene / physics APIs.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § AstralProjectionStateSystem
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AstralProjectionState {
  readonly isProjecting: boolean;
  readonly bodyPosition: { x: number; y: number };
  readonly projectionPosition: { x: number; y: number };
  readonly markedEnemyIds: ReadonlySet<string>;
  readonly maxMarks: number;
  readonly remainingMs: number;
}

/** Adapter for projection entity management (injected, not hardcoded). */
export interface ProjectionEntityAdapter {
  /** Spawn the ethereal projection at the player's current position. */
  spawnProjection(x: number, y: number): void;
  /** Destroy the projection entity. */
  despawnProjection(): void;
  /** Lock the body at position and mark it invincible. */
  lockBody(x: number, y: number): void;
  /** Restore body control (un-lock, remove invincibility). */
  unlockBody(): void;
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class AstralProjectionStateSystem {
  private _isProjecting = false;
  private _bodyPosition: { x: number; y: number } = { x: 0, y: 0 };
  private _projectionPosition: { x: number; y: number } = { x: 0, y: 0 };
  private _markedEnemyIds = new Set<string>();
  private _maxMarks = 0;
  private _remainingMs = 0;

  private readonly _adapter: ProjectionEntityAdapter;

  constructor(adapter: ProjectionEntityAdapter) {
    this._adapter = adapter;
  }

  // ---------------------------------------------------------------------------
  // Projection control
  // ---------------------------------------------------------------------------

  /**
   * Begin projection: lock body at current position, spawn projection entity.
   * @param x          Player's current world x (body lock position).
   * @param y          Player's current world y (body lock position).
   * @param maxMarks   Maximum enemies that can be marked during this projection.
   * @param durationMs How long the projection window lasts.
   */
  beginProjection(x: number, y: number, maxMarks: number, durationMs: number): void {
    if (this._isProjecting) return; // already active — ignore re-entry

    this._isProjecting = true;
    this._bodyPosition = { x, y };
    this._projectionPosition = { x, y };
    this._markedEnemyIds = new Set();
    this._maxMarks = maxMarks;
    this._remainingMs = durationMs;

    this._adapter.lockBody(x, y);
    this._adapter.spawnProjection(x, y);
  }

  /**
   * Mark an enemy during the projection phase.
   * Called when the projection entity overlaps an enemy.
   * No-op if mark cap is already reached.
   */
  markEnemy(enemyId: string): void {
    if (!this._isProjecting) return;
    if (this._markedEnemyIds.size >= this._maxMarks) return;
    this._markedEnemyIds.add(enemyId);
  }

  /**
   * Update the projection's current world position (called each frame
   * when the player moves the projection).
   */
  setProjectionPosition(x: number, y: number): void {
    this._projectionPosition = { x, y };
  }

  /**
   * End projection: restore body control, despawn projection.
   * @returns Set of marked enemy IDs for the follow-up burst.
   */
  endProjection(): Set<string> {
    if (!this._isProjecting) return new Set();

    const marks = new Set(this._markedEnemyIds);
    this._isProjecting = false;
    this._markedEnemyIds = new Set();
    this._remainingMs = 0;

    this._adapter.despawnProjection();
    this._adapter.unlockBody();

    return marks;
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  get state(): AstralProjectionState {
    return {
      isProjecting: this._isProjecting,
      bodyPosition: { ...this._bodyPosition },
      projectionPosition: { ...this._projectionPosition },
      markedEnemyIds: this._markedEnemyIds,
      maxMarks: this._maxMarks,
      remainingMs: this._remainingMs,
    };
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Per-frame: tick the projection window down.
   * Auto-ends the projection when time runs out.
   * @param delta Frame delta in ms.
   */
  update(delta: number): void {
    if (!this._isProjecting) return;
    this._remainingMs = Math.max(0, this._remainingMs - delta);
    if (this._remainingMs <= 0) {
      this.endProjection();
    }
  }

  /** Release all state. */
  destroy(): void {
    if (this._isProjecting) {
      this._adapter.despawnProjection();
      this._adapter.unlockBody();
    }
    this._isProjecting = false;
    this._markedEnemyIds = new Set();
    this._remainingMs = 0;
  }
}
